// ============================================================
// ModuleRoleMappingsSection.jsx  (role-model expansion — shared extraction, 2026-07-13)
//
// THE shared shape for a per-module "role mappings" section in the Group
// Mappings tab: map an AD security group -> a module role; federation
// JIT/reconcile reads these to auto-grant the module-role overlay. Extracted
// from the three near-identical clones (CarePeople / CareCharge / CarePlan) so
// each is now a thin instantiation — and a FUTURE module-role section (e.g.
// the named-not-built CareRota rota_coordinator) drops in as one more
// instantiation, not a fourth hand-clone.
//
// Shape: Group dropdown (GUID fallback) → Module role dropdown → [optional
// section-specific extra fields] → Create → mappings table
// (GROUP / OBJECT ID / MODULE ROLE / [optional extra cols] / CREATED) + revoke.
//
// The ONE known divergence — CarePeople's "Scope tier" control + TIER column —
// composes in via the optional `extra` slot; CareCharge and CarePlan pass no
// extra and render without it. Behaviour is preserved per section: the button
// labels (Add mapping / Create mapping / Cancel / Delete / Remove mapping) and
// the field labels (Group / Module role) are identical across all three, so
// they stay literal here; everything section-specific is a prop.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import Field from '../components/Field';
import DataTable from '../components/DataTable';
import { toast } from '../components/Toaster';
import { listAvailableGroups } from '../api/federation';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function truncateGuid(g) { return g && g.length > 13 ? `${g.slice(0, 8)}…${g.slice(-4)}` : g; }

/**
 * @param {object}   props
 * @param {number}   props.clientId
 * @param {string}   props.testId        section wrapper data-testid (e.g. 'carepeople-role-mappings')
 * @param {string}   props.title         section heading
 * @param {string}   props.descriptor    muted sub-heading text
 * @param {string}   props.roleField     the body/list-row key for the role
 *                                       ('carePeopleRole' | 'careChargeRole' | 'carePlanRole')
 * @param {Array<{value,label}>} props.roleOptions   the Module-role dropdown options (+ table label map)
 * @param {string}   props.emptyMessage  DataTable empty state
 * @param {string}   props.confirmTitle  delete-confirm title
 * @param {(target)=>string} props.confirmMessage  delete-confirm message
 * @param {object}   props.api           { list(clientId), create(clientId, body), remove(clientId, mappingId) }
 * @param {object=}  props.extra         OPTIONAL section-specific extra fields:
 *   { initialForm:object, normalize?(form)->form, renderFields(form,setField)->JSX,
 *     buildBody(form)->partialBody, columns:[DataTable column defs] }
 */
export default function ModuleRoleMappingsSection({
  clientId, testId, title, descriptor, roleField, roleOptions,
  emptyMessage, confirmTitle, confirmMessage, api, extra,
  toastCreated = 'Role mapping created.', toastRemoved = 'Role mapping removed.',
}) {
  const roleLabelMap = Object.fromEntries(roleOptions.map((o) => [o.value, o.label]));
  const baseForm = { groupObjectId: '', groupDisplayName: '', role: roleOptions[0].value, ...(extra ? extra.initialForm : {}) };

  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [groups, setGroups] = useState([]);
  const [pickerFailed, setPickerFailed] = useState(false); // federation error → GUID fallback

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(baseForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.list(clientId);
      setMappings(res.mappings || []);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
    // `api` is a stable per-section binding object; `clientId` is the real dep.
    // (The admin CRA lint config doesn't ship react-hooks/exhaustive-deps, so
    // no disable directive — `api` intentionally omitted from the dep array.)
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const loadGroups = useCallback(async () => {
    try {
      const res = await listAvailableGroups(clientId);
      setGroups(res.groups || []);
      setPickerFailed(false);
    } catch (_err) {
      setPickerFailed(true); // fall back to manual GUID entry
    }
  }, [clientId]);

  function setField(key, value) {
    setForm((f) => {
      let next = { ...f, [key]: value };
      if (extra && extra.normalize) next = extra.normalize(next);
      return next;
    });
  }

  function openCreate() {
    setForm(baseForm);
    setFormError(null);
    setCreating(true);
    loadGroups();
  }

  function onPickGroup(id) {
    const g = groups.find((x) => x.id === id);
    setField('groupObjectId', id);
    setField('groupDisplayName', (g && g.displayName) || '');
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormError(null);
    if (!GUID_REGEX.test(form.groupObjectId)) { setFormError('Select a group or enter a valid group GUID.'); return; }
    setSubmitting(true);
    try {
      const body = { groupObjectId: form.groupObjectId.toLowerCase(), [roleField]: form.role };
      if (form.groupDisplayName) body.groupDisplayName = form.groupDisplayName;
      if (extra && extra.buildBody) Object.assign(body, extra.buildBody(form));
      await api.create(clientId, body);
      toast.success(toastCreated);
      setCreating(false);
      await load();
    } catch (err) {
      setFormError(translateError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await api.remove(clientId, deleteTarget.mappingId);
      toast.success(toastRemoved);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setDeleteSubmitting(false);
    }
  }

  const mappedGroupIds = new Set(mappings.map((m) => m.groupObjectId));

  const columns = [
    { key: 'group', label: 'Group', render: (r) => r.groupDisplayName || <span className="group-mappings-tab__muted">—</span> },
    { key: 'guid', label: 'Object ID', render: (r) => <span title={r.groupObjectId} style={{ fontFamily: 'monospace', fontSize: 12 }}>{truncateGuid(r.groupObjectId)}</span> },
    { key: 'role', label: 'Module role', render: (r) => <Badge variant="teal">{roleLabelMap[r[roleField]] || r[roleField]}</Badge> },
    ...(extra && extra.columns ? extra.columns : []),
    { key: 'created', label: 'Created', render: (r) => formatDate(r.createdAt) },
    { key: 'actions', label: '', render: (r) => (
      <button type="button" className="user-agent-access__row-action user-agent-access__row-action--danger" onClick={() => setDeleteTarget(r)}>Delete</button>
    ) },
  ];

  return (
    <section style={{ marginTop: 'var(--space-8)' }} data-testid={testId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <div>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <p className="group-mappings-tab__muted" style={{ margin: '4px 0 0', fontSize: 13 }}>{descriptor}</p>
        </div>
        {!creating && (
          <button type="button" className="user-agent-access__grant" onClick={openCreate}>
            <Plus size={14} /> Add mapping
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={handleCreate} className="group-mappings-tab__form" style={{ marginBottom: 'var(--space-5)' }}>
          {!pickerFailed ? (
            <Field label="Group">
              <select value={form.groupObjectId} onChange={(e) => onPickGroup(e.target.value)} aria-label="Group">
                <option value="" disabled>Select a group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id} disabled={mappedGroupIds.has(g.id)}>
                    {(g.displayName || '(no display name)')} — {truncateGuid(g.id)}{mappedGroupIds.has(g.id) ? ' (mapped)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              <Field label="M365 security group GUID" help="The group picker is unavailable (federation not configured/consented). Enter the group GUID manually.">
                <input type="text" value={form.groupObjectId} onChange={(e) => setField('groupObjectId', e.target.value)} aria-label="M365 security group GUID" />
              </Field>
              <Field label="Display name (optional)">
                <input type="text" value={form.groupDisplayName} onChange={(e) => setField('groupDisplayName', e.target.value)} aria-label="Display name" />
              </Field>
            </>
          )}

          <Field label="Module role">
            <select value={form.role} onChange={(e) => setField('role', e.target.value)} aria-label="Module role">
              {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          {extra && extra.renderFields ? extra.renderFields(form, setField) : null}

          {formError && <p style={{ color: 'var(--color-error-dark)', fontSize: 13 }} role="alert">{formError}</p>}

          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button type="button" className="user-agent-access__cancel" onClick={() => setCreating(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="user-agent-access__submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create mapping'}</button>
          </div>
        </form>
      )}

      <DataTable
        data={mappings}
        columns={columns}
        rowKey={(r) => r.mappingId}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage={emptyMessage}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => { if (!deleteSubmitting) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title={confirmTitle}
        message={deleteTarget ? confirmMessage(deleteTarget) : ''}
        confirmLabel="Remove mapping"
        isSubmitting={deleteSubmitting}
      />
    </section>
  );
}
